import React, { useState, useEffect } from "react";
import { CardNumberElement, CardExpiryElement, CardCvcElement, PaymentRequestButtonElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";

import LogoMark from "../../gallery/default/logomark.png";
import { useSelector } from "react-redux";
import CountdownTimer from "../events/CountdownTimer";
import { useHistory } from "react-router-dom";
import { BeatLoader } from "react-spinners";

export default function PaymentForm() {
  const history = useHistory();
  const cart = useSelector((state) => state.event.cart);
  const currentEvent = useSelector((state) => state.event.currentEvent);
  const isTimer = useSelector((state) => state.timer.isTimer);
  const booking = useSelector((state) => state.event.booking);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const bookingId = booking ? booking.id : null;

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stripeLoading, setStripeLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (stripe && elements) {
      setStripeLoading(false);

      const pr = stripe.paymentRequest({
        country: "GB",
        currency: "gbp",
        total: {
          label: currentEvent.name,
          amount: Math.round(cart.grandTotal * 100), // amount in pence
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check for wallet support
      pr.canMakePayment().then((result) => {
        if (result && (result.applePay || result.googlePay)) {
          setPaymentRequest(pr);
          setCanMakePayment(true);
        }
      });

      // Handle payment method selection
      const handlePaymentMethod = async (event) => {
        setLoading(true);
        setError("");

        try {
          // Get client secret from backend
          const { data } = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/api/stripe/create-payment-intent`,
            { booking_id: bookingId },
            {
              headers: { Authorization: process.env.REACT_APP_API_KEY },
            }
          );

          const clientSecret = data.client_secret;

          // Confirm payment
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: event.paymentMethod.id,
            },
            { handleActions: false }
          );

          if (error) {
            event.complete("fail");
            setError(error.message);
          } else {
            event.complete("success");
            if (paymentIntent.status === "succeeded") {
              setRedirecting(true);
              const closeModalButton = document.getElementById("close_modal");
              if (closeModalButton) {
                closeModalButton.click();
              }
              // Navigate immediately for faster redirect
              history.push(`/ticket/${booking.slug}`);
            }
          }
        } catch (err) {
          event.complete("fail");
          setError("Payment failed, please try again.");
        }

        setLoading(false);
      };

      pr.on("paymentmethod", handlePaymentMethod);

      // Cleanup function
      return () => {
        pr.off("paymentmethod", handlePaymentMethod);
      };
    }
  }, [stripe, elements, bookingId, cart.grandTotal, currentEvent.name, booking.slug, history]);

  const elementStyle = {
    style: {
      invalid: {
        color: "#e53e3e",
      },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    try {
      // Get client secret from backend
      const { data } = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/stripe/create-payment-intent`,
        { booking_id: bookingId },
        {
          headers: { Authorization: process.env.REACT_APP_API_KEY },
        }
      );

      const clientSecret = data.client_secret;

      // Confirm payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
        setRedirecting(true);

        setTimeout(() => {
          document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
          document.body.classList.remove("modal-open");
          document.body.style.removeProperty("overflow");
          document.body.style.removeProperty("padding-right");

          history.push(`/ticket/${booking.slug}`);
        }, 400);
      }
    } catch (err) {
      setError("Payment failed, please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="row">
        <div className="col-12">
          <div className="col-12 d-flex justify-content-center mb-3">
            <img src={LogoMark} alt="Sahi Search" />
          </div>
          {isTimer && !isIOS && <CountdownTimer />}
          <p className="not-reserved">Tickets are not reserved until you have completed payment.</p>
          <h3 className="login-event-title">{currentEvent.name}</h3>
          <p className="ticket-waiting">
            {cart.productsCount}&nbsp;
            {cart.productsCount === 1 ? "ticket waiting" : "tickets waiting"}
          </p>
          <p className="inclusive-fee">All ticket prices are inclusive of booking fees</p>
          <h3 className="grand-total">
            {cart.discount > 0 && <span className="discount-applied-success">£{cart.subTotal.toFixed(2)}</span>}
            &nbsp; £{cart.grandTotal.toFixed(2)}
          </h3>
          {redirecting ? (
            <div className="text-center mt-4">
              <BeatLoader color="#C6153B" />
              <h4 className="mt-3" style={{ color: "#C6153B" }}>
                Payment Successful! Redirecting to ticket...
              </h4>
            </div>
          ) : stripeLoading ? (
            <div className="text-center mt-4">
              <BeatLoader color="#C6153B" />
              <h4 className="mt-3" style={{ color: "#C6153B" }}>
                Loading Payment Form...
              </h4>
            </div>
          ) : (
            <>
              {canMakePayment && (
                <div className="row mb-3">
                  <div className="col-12">
                    <PaymentRequestButtonElement options={{ paymentRequest }} className="PaymentRequestButton" />
                  </div>
                </div>
              )}
              {canMakePayment && (
                <div className="row mb-3">
                  <div className="col-12 text-center">
                    <span className="or-divider">or</span>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label">Card Number</label>
                    <CardNumberElement className="form-control input-text-size" options={elementStyle} />
                  </div>

                  <div className="col-md-6 col-12 mb-3">
                    <label className="form-label">Expiry</label>
                    <CardExpiryElement className="form-control input-text-size" options={elementStyle} />
                  </div>
                  <div className="col-md-6 col-12 mb-3">
                    <label className="form-label">CVC</label>
                    <CardCvcElement className="form-control input-text-size" options={elementStyle} />
                  </div>

                  {error && <p className="text-danger mt-2">{error}</p>}

                  <div className="col-12 mb-3">
                    <button type="submit" className={loading ? "btn btn-disable-login mb-3" : "btn btn-login mb-3"}>
                      Pay Now
                    </button>
                    {loading && (
                      <div className="text-center">
                        <BeatLoader color="#C6153B" />
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
